import React from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { DateFilterTypeEnum } from '../../../../core/enums/date-filter-type.enum'
import { ReactComponent as ChevronIcon } from '../../../../assets/icons/app/bottom-arrow-light.icon.svg'
import { ReactComponent as SelectedIcon } from '../../../../assets/icons/selected.icon.svg'
import GlHDivider from '../../../share/GlHDivider'

const MenuStyle = styled.div`
  max-height: 100%;
  width: 256px;
`

const HeaderAccordionStyle = styled.div`
  color: #595959;
  font-family: Fira Sans, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 19.2px */
  display: flex;
  flex-direction: row;
  max-height: 100%;
  width: 100%;
  height: 28px;
  padding: 6px 8px 6px 12px;
  background: ${({ open }: { open: boolean }) =>
    open ? '#f7f7f7' : '#ffffff'};

  &:hover {
    background: #f7f7f7;
  }
  &:active {
    background: #e3e3e3;
  }
  .icon-header {
    transform: ${({ open }: { open: boolean }) =>
      (open && 'rotate(0)') || 'rotate(180deg)'};
  }
`

const LabelStyle = styled.span`
  color: #252525;
  font-family: Fira Sans, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 19.2px */
`

const ItemStyle = styled.span`
  padding: 6px 12px 6px 24px;
  &:hover {
    background: #f7f7f7;
  }
  &:active {
    background: #e3e3e3;
  }
`

const BodyAccordionStyle = styled.div`
  display: ${({ open }: { open: boolean }) => (open ? 'flex' : 'none')};
  max-height: ${({ open }: { open: boolean }) => (open ? '300px' : '0')};
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`

const BetweenContainerStyle = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const BetweenColumnStyle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  &:first-of-type {
    border-right: 1px solid #e3e3e3;
  }
`

const BetweenLabelStyle = styled.span`
  color: #595959;
  font-family: Fira Sans, sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  padding: 4px 12px 4px 24px;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
`

interface Props {
  yearsOptions: any
  yearsOptionsSelected: any
  yearTypeSelected: DateFilterTypeEnum | null
  handleYearTypeSelection: (value: DateFilterTypeEnum | null) => void
  handleChangeListFilterSelected: (
    newList: Array<string | number | null>,
  ) => void
}

const DynamicsResultsYearFiltersActionsV2 = ({
  yearsOptions,
  yearsOptionsSelected,
  yearTypeSelected,
  handleYearTypeSelection,
  handleChangeListFilterSelected,
}: Props) => {
  const { t } = useTranslation()

  const handleBetweenSelection = (
    yearValue: string | number,
    isStart: boolean,
  ) => {
    const newList = [...yearsOptionsSelected]
    if (isStart) {
      newList[0] = yearValue === yearsOptionsSelected[0] ? null : yearValue
    } else {
      newList[1] = yearValue === yearsOptionsSelected[1] ? null : yearValue
    }
    handleChangeListFilterSelected(newList)
  }

  return (
    <MenuStyle className="flex flex-col bg-white text-black rounded">
      <div>
        <HeaderAccordionStyle
          className="rounded-t items-center justify-between"
          open={yearTypeSelected === DateFilterTypeEnum.IN}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            handleYearTypeSelection(DateFilterTypeEnum.IN)
          }}
        >
          <div className="flex flex-row gap-1">
            <span>{t('webApp.dynamics.topsResults.filter.in')}</span>
            {(yearTypeSelected === DateFilterTypeEnum.IN &&
              yearsOptionsSelected[0] && (
                <LabelStyle>{yearsOptionsSelected[0]}</LabelStyle>
              )) ||
              null}
          </div>
          <ChevronIcon className="icon-header w-4 h-4" />
        </HeaderAccordionStyle>
        <GlHDivider />
        <BodyAccordionStyle
          className="flex flex-col"
          open={yearTypeSelected === DateFilterTypeEnum.IN}
        >
          {yearsOptions.map((yearOption: any) => (
            <ItemStyle
              key={`${yearOption.value}_in`}
              className="flex flex-row items-center justify-between"
              onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                event.preventDefault()
                handleChangeListFilterSelected([
                  yearOption.value === yearsOptionsSelected[0]
                    ? null
                    : yearOption.value,
                  null,
                ])
              }}
            >
              <LabelStyle>{yearOption.label}</LabelStyle>
              {(yearOption.value === yearsOptionsSelected[0] && (
                <SelectedIcon className="w-4 h-4" />
              )) ||
                null}
            </ItemStyle>
          ))}
        </BodyAccordionStyle>
      </div>
      <div>
        <GlHDivider />
        <HeaderAccordionStyle
          className="rounded-t items-center justify-between"
          open={yearTypeSelected === DateFilterTypeEnum.BEFORE}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            handleYearTypeSelection(DateFilterTypeEnum.BEFORE)
          }}
        >
          <div className="flex flex-row gap-1">
            <span>{t('webApp.dynamics.topsResults.filter.before')}</span>
            {(yearTypeSelected === DateFilterTypeEnum.BEFORE &&
              yearsOptionsSelected[0] && (
                <LabelStyle>{yearsOptionsSelected[0]}</LabelStyle>
              )) ||
              null}
          </div>

          <ChevronIcon className="icon-header w-4 h-4" />
        </HeaderAccordionStyle>
        <GlHDivider />
        <BodyAccordionStyle
          className="flex flex-col"
          open={yearTypeSelected === DateFilterTypeEnum.BEFORE}
        >
          {yearsOptions.map((yearOption: any) => (
            <ItemStyle
              key={`${yearOption.value}_before`}
              className="flex flex-row items-center justify-between"
              onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                event.preventDefault()
                handleChangeListFilterSelected([
                  yearOption.value === yearsOptionsSelected[0]
                    ? null
                    : yearOption.value,
                  null,
                ])
              }}
            >
              <LabelStyle>{yearOption.label}</LabelStyle>
              {(yearOption.value === yearsOptionsSelected[0] && (
                <SelectedIcon className="w-4 h-4" />
              )) ||
                null}
            </ItemStyle>
          ))}
        </BodyAccordionStyle>
      </div>

      <div>
        <GlHDivider />
        <HeaderAccordionStyle
          className="rounded-t items-center justify-between"
          open={yearTypeSelected === DateFilterTypeEnum.AFTER}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            handleYearTypeSelection(DateFilterTypeEnum.AFTER)
          }}
        >
          <div className="flex flex-row gap-1">
            <span>{t('webApp.dynamics.topsResults.filter.after')}</span>
            {(yearTypeSelected === DateFilterTypeEnum.AFTER &&
              yearsOptionsSelected[0] && (
                <LabelStyle>{yearsOptionsSelected[0]}</LabelStyle>
              )) ||
              null}
          </div>
          <ChevronIcon className="icon-header w-4 h-4" />
        </HeaderAccordionStyle>
        <GlHDivider />
        <BodyAccordionStyle
          className="flex flex-col"
          open={yearTypeSelected === DateFilterTypeEnum.AFTER}
        >
          {yearsOptions.map((yearOption: any) => (
            <ItemStyle
              key={`${yearOption.value}after`}
              className="flex flex-row items-center justify-between"
              onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                event.preventDefault()
                handleChangeListFilterSelected([
                  yearOption.value === yearsOptionsSelected[0]
                    ? null
                    : yearOption.value,
                  null,
                ])
              }}
            >
              <LabelStyle>{yearOption.label}</LabelStyle>
              {((yearOption.value === yearsOptionsSelected[0] ||
                yearOption.value === yearsOptionsSelected[1]) && (
                <SelectedIcon className="w-4 h-4" />
              )) ||
                null}
            </ItemStyle>
          ))}
        </BodyAccordionStyle>
      </div>

      <div>
        <GlHDivider />
        <HeaderAccordionStyle
          className="rounded-t items-center justify-between"
          open={yearTypeSelected === DateFilterTypeEnum.BETWEEN}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            handleYearTypeSelection(DateFilterTypeEnum.BETWEEN)
          }}
        >
          <div className="flex flex-row gap-1">
            <span>{t('webApp.dynamics.topsResults.filter.between')}</span>
            {yearTypeSelected === DateFilterTypeEnum.BETWEEN &&
              (yearsOptionsSelected[0] || yearsOptionsSelected[1]) && (
                <LabelStyle>
                  {yearsOptionsSelected[0] || '?'} -{' '}
                  {yearsOptionsSelected[1] || '?'}
                </LabelStyle>
              )}
          </div>
          <ChevronIcon className="icon-header w-4 h-4" />
        </HeaderAccordionStyle>
        <GlHDivider />
        <BodyAccordionStyle
          className="flex flex-col"
          open={yearTypeSelected === DateFilterTypeEnum.BETWEEN}
        >
          <BetweenContainerStyle>
            <BetweenColumnStyle>
              <BetweenLabelStyle>
                {t('webApp.dynamics.topsResults.filter.from')}
              </BetweenLabelStyle>
              {yearsOptions.map((yearOption: any) => (
                <ItemStyle
                  key={`${yearOption.value}_start_between`}
                  className="flex flex-row items-center justify-between"
                  onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                    event.preventDefault()
                    handleBetweenSelection(yearOption.value, true)
                  }}
                >
                  <LabelStyle>{yearOption.label}</LabelStyle>
                  {(yearOption.value === yearsOptionsSelected[0] && (
                    <SelectedIcon className="w-4 h-4" />
                  )) ||
                    null}
                </ItemStyle>
              ))}
            </BetweenColumnStyle>

            <BetweenColumnStyle>
              <BetweenLabelStyle>
                {t('webApp.dynamics.topsResults.filter.to')}
              </BetweenLabelStyle>
              {yearsOptions.map((yearOption: any) => (
                <ItemStyle
                  key={`${yearOption.value}_end_between`}
                  className="flex flex-row items-center justify-between"
                  onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                    event.preventDefault()
                    handleBetweenSelection(yearOption.value, false)
                  }}
                >
                  <LabelStyle>{yearOption.label}</LabelStyle>
                  {(yearOption.value === yearsOptionsSelected[1] && (
                    <SelectedIcon className="w-4 h-4" />
                  )) ||
                    null}
                </ItemStyle>
              ))}
            </BetweenColumnStyle>
          </BetweenContainerStyle>
        </BodyAccordionStyle>
      </div>
    </MenuStyle>
  )
}

export default DynamicsResultsYearFiltersActionsV2
